import { useState, useRef, useEffect, useMemo } from "react";
import { TreeCategory } from "./TreeCategory";
import { cleanUrl } from "../contants";

type Brand = {
  brandId: string;
  brandName: string;
  attachment?: { fileUrl?: string; fileName?: string };
};

type ProductFilterProps = {
  onChangeFilter: (filters: any) => void;
  dataBrand: any;
  dataCategory: any;
  keyword?: string;
  initialKeyword?: string;
  initialBrandIds?: string[];
  initialCategoryIds?: string[];
  initialInventoryStatus?: number | null;
};

export default function ProductFilter({
  onChangeFilter,
  dataBrand,
  dataCategory,
  // keyword is accepted for backward compatibility but handled via initialKeyword
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  keyword: _keyword,
  initialKeyword = "",
  initialBrandIds,
  initialCategoryIds,
  initialInventoryStatus = null,
}: ProductFilterProps) {
  const normalizedInitialBrandIds = useMemo(
    () => initialBrandIds ?? [],
    [initialBrandIds]
  );
  const normalizedInitialCategoryIds = useMemo(
    () => initialCategoryIds ?? [],
    [initialCategoryIds]
  );

  const [keyword, setKeyword] = useState(initialKeyword);
  const [brandIds, setBrandIds] = useState<string[]>(normalizedInitialBrandIds);
  const [categoryIds, setCategoryIds] = useState<string[]>(
    normalizedInitialCategoryIds
  );
  const [inventoryStatus, setInventoryStatus] = useState<number | null>(
    initialInventoryStatus
  );

  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  useEffect(() => {
    setBrandIds(normalizedInitialBrandIds);
  }, [normalizedInitialBrandIds]);

  useEffect(() => {
    setCategoryIds(normalizedInitialCategoryIds);
  }, [normalizedInitialCategoryIds]);

  useEffect(() => {
    setInventoryStatus(initialInventoryStatus);
  }, [initialInventoryStatus]);

  const [openCategory, setOpenCategory] = useState(false);
  const [openBrand, setOpenBrand] = useState(false);

  const categoryRef = useRef<any>(null);
  const brandRef = useRef<any>(null);

  const brandTriggerRef = useRef<any>(null);
  const categoryTriggerRef = useRef<any>(null);

  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const traverse = (nodes: any[]) => {
      nodes?.forEach((node) => {
        map.set(node.id, node.name);
        if (node.children?.length) traverse(node.children);
      });
    };
    traverse(dataCategory || []);
    return map;
  }, [dataCategory]);

  const categoryLabel = useMemo(() => {
    if (!categoryIds.length) return "Nhóm sản phẩm";
    const uniqueNames = Array.from(
      new Set(
        categoryIds
          .map((id) => categoryNameMap.get(id))
          .filter(Boolean) as string[]
      )
    );
    const display = uniqueNames.slice(0, 2).join(", ");
    return uniqueNames.length > 2
      ? `${display} +${uniqueNames.length - 2}`
      : display;
  }, [categoryIds, categoryNameMap]);

  const brandNameMap = useMemo(() => {
    const map = new Map<string, string>();
    dataBrand?.forEach((brand: Brand) =>
      map.set(brand.brandId, brand.brandName)
    );
    return map;
  }, [dataBrand]);

  const brandLabel = useMemo(() => {
    if (!brandIds.length) return "Thương hiệu";
    const uniqueNames = Array.from(
      new Set(
        brandIds
          .map((id) => brandNameMap.get(id))
          .filter(Boolean) as string[]
      )
    );
    const display = uniqueNames.slice(0, 2).join(", ");
    return uniqueNames.length > 2
      ? `${display} +${uniqueNames.length - 2}`
      : display;
  }, [brandIds, brandNameMap]);

  useEffect(() => {
    function handleClick(e: any) {
      if (
        categoryRef.current &&
        !categoryRef.current.contains(e.target) &&
        !categoryTriggerRef.current?.contains(e.target)
      ) {
        setOpenCategory(false);
      }
      if (
        brandRef.current &&
        !brandRef.current.contains(e.target) &&
        !brandTriggerRef.current?.contains(e.target)
      ) {
        setOpenBrand(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const getAllChildIds = (node: any): string[] => {
    let ids: string[] = [];
    if (!node.children || node.children.length === 0) return ids;
    for (let child of node.children) {
      ids.push(child.id);
      ids = ids.concat(getAllChildIds(child));
    }
    return ids;
  };

  const toggleCategory = (node: any) => {
    const ids = [node.id, ...getAllChildIds(node)];
    let newSelected = [...categoryIds];
    const checked = ids.every((id) => newSelected.includes(id));
    if (checked) {
      newSelected = newSelected.filter((id) => !ids.includes(id));
    } else {
      ids.forEach((id) => {
        if (!newSelected.includes(id)) newSelected.push(id);
      });
    }
    setCategoryIds(newSelected);
  };

  const toggleBrand = (id: string) => {
    setBrandIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  };

  const handleSearch = () => {
    onChangeFilter({
      keyword,
      brandIds,
      categoryIds,
      inventoryStatus,
    });
  };

  return (
    <div className="w-full mb-6">
      <div className="flex flex-wrap gap-4 md:flex-nowrap md:items-end">
        {/* CATEGORY */}
        <div className="flex-grid-item relative" ref={categoryRef}>
          <div
            className="wcf-horizontal-popover"
            ref={categoryTriggerRef}
            onClick={() => {
              setOpenCategory(!openCategory);
              setOpenBrand(false);
            }}
          >
            {categoryLabel}
          </div>

          {openCategory && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute top-[110%] left-0 w-[260px] bg-white border-gray-300 shadow-xl rounded-xl border p-3 z-50 max-h-64 overflow-y-auto"
            >
              <TreeCategory
                nodes={dataCategory}
                categoryIds={categoryIds}
                toggleCategory={toggleCategory}
              />
            </div>
          )}
        </div>

        {/* BRAND */}
        <div className="flex-grid-item relative" ref={brandRef}>
          <div
            ref={brandTriggerRef}
            className="wcf-horizontal-popover"
            onClick={() => {
              setOpenBrand(!openBrand);
              setOpenCategory(false);
            }}
          >
            {brandLabel}
          </div>

          {openBrand && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute top-[110%] left-0 w-[260px] bg-white border-gray-300 shadow-xl rounded-xl border p-3 z-50 max-h-64 overflow-y-auto"
            >
              {dataBrand?.map((item: Brand) => {
                const url = cleanUrl(item?.attachment?.fileUrl ?? "");
                const checked = brandIds.includes(item.brandId);
                return (
                  <label
                    key={item.brandId}
                    className="flex items-center gap-3 py-1 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleBrand(item.brandId)}
                      style={{ margin: "0px" }}
                    />
                    {url ? (
                      <img
                        src={url}
                        alt={item.brandName}
                        className="h-8 w-14 object-contain rounded-md border border-gray-200"
                      />
                    ) : (
                      <span className="h-8 w-14 grid place-items-center rounded-md border border-dashed border-gray-200 text-[11px] text-gray-500">
                        Logo
                      </span>
                    )}
                    <span className="text-sm text-gray-700">{item?.brandName}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* STOCK */}
        <div className="flex-grid-item">
          <select
            className="wcf-horizontal-popover"
            style={{ margin: "0px" }}
            onChange={(e) =>
              setInventoryStatus(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">Tình trạng tồn</option>
            <option value={0}>Còn hàng</option>
            <option value={1}>Hết hàng</option>
          </select>
        </div>

        {/* KEYWORD */}
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Tìm kiếm"
            className="border border-gray-300 rounded-lg text-sm w-full "
            style={{
              borderRadius: "8px",
              margin: "0px",
              padding: "6px 40px 6px 20px",
              fontSize: "15px",
            }}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>

        {/* BUTTON */}
        <button
          onClick={handleSearch}
          className="flex-grid-item w-full md:w-auto bg-[#2070ba] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#165a97] transition"
          style={{
            backgroundColor: "#2070ba",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            margin: "0px",
            transition: "background-color 0.2s ease-in-out",
          }}
        >
          Tìm
        </button>
      </div>
    </div>
  );
}
